import React, { useEffect, useState, useRef } from 'react';
import { Check, Checks, Heart, XCircle, Sparkle, CaretDown, Clock } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import type { OfferStatus } from '../../lib/utils';
interface OfferStatusPillProps {
  status: OfferStatus;
  variant?: 'sender' | 'receiver';
  onChange?: (newStatus: OfferStatus) => void;
}
export function OfferStatusPill({
  status,
  variant = 'sender',
  onChange
}: OfferStatusPillProps) {
  const isSender = variant === 'sender';
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  const isTerminal = status === 'succes' || status === 'archived';
  const handleToggle = (e: React.MouseEvent) => {
    if (isSender || !onChange || isTerminal) return;
    e.stopPropagation();
    setIsOpen(!isOpen);
  };
  const handleSelect = (e: React.MouseEvent, newStatus: OfferStatus) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onChange) {
      onChange(newStatus);
    }
  };
  const getSenderDisplay = () => {
    switch (status) {
      case 'new':
        return {
          icon: <Check size={16} weight="bold" className="text-light-4" data-id="element-657" />,
          label: 'Trimis',
          colorClass: 'text-light-3'
        };
      case 'saved':
        return {
          icon: <Heart size={16} weight="fill" className="text-lime-1" data-id="element-658" />,
          label: 'Interesat',
          colorClass: 'text-lime-1'
        };
      case 'postponed':
        return {
          icon: <Checks size={16} weight="bold" className="text-lime-1" data-id="element-659" />,
          label: 'Vizualizat',
          colorClass: 'text-lime-1'
        };
      case 'rejected':
        return {
          icon: <XCircle size={16} weight="fill" className="text-red-1" data-id="element-660" />,
          label: 'Respins',
          colorClass: 'text-red-1'
        };
      case 'succes':
        return {
          icon: <Sparkle size={16} weight="fill" className="text-lime-1" data-id="element-661" />,
          label: 'Închiriat',
          colorClass: 'text-lime-1'
        };
      case 'archived':
        return {
          icon: <Check size={16} weight="bold" className="text-light-4" data-id="element-662" />,
          label: 'Arhivată',
          colorClass: 'text-light-4'
        };
    }
  };
  const getReceiverDisplay = (s: OfferStatus) => {
    switch (s) {
      case 'new':
        return {
          icon: <Sparkle size={18} weight="fill" className="text-lime-1" data-id="element-663" />,
          label: 'Nou'
        };
      case 'saved':
        return {
          icon: <Heart size={18} weight="fill" className="text-lime-1" data-id="element-664" />,
          label: 'Salvat'
        };
      case 'postponed':
        return {
          icon: <Clock size={18} weight="regular" className="text-light-3" data-id="element-665" />,
          label: 'Amânat'
        };
      case 'rejected':
        return {
          icon: <XCircle size={18} weight="fill" className="text-red-1" data-id="element-666" />,
          label: 'Respins'
        };
      case 'succes':
        return {
          icon: <Sparkle size={18} weight="fill" className="text-lime-1" data-id="element-667" />,
          label: 'Închiriat'
        };
      case 'archived':
        return {
          icon: <Check size={16} weight="bold" className="text-light-4" data-id="element-668" />,
          label: 'Arhivată'
        };
    }
  };
  if (isSender) {
    const display = getSenderDisplay();
    return <div className="flex items-center gap-1.5" title={display.label} data-id="element-669">
        {display.icon}
        <span className={cn('text-xs font-medium', display.colorClass)} data-id="element-670">
          {display.label}
        </span>
      </div>;
  }
  // Receiver view
  const currentDisplay = getReceiverDisplay(status);
  // Terminal states render as static (non-interactive) pills.
  if (isTerminal) {
    return <div className={cn('inline-flex items-center gap-1.5 h-9 px-3 rounded-lg', status === 'succes' ? 'bg-lime-5 border border-lime-4' : 'bg-dark-3 border border-dark-4')} title={currentDisplay.label} data-id="element-671">
        {currentDisplay.icon}
        <span className={cn('text-xs font-medium', status === 'succes' ? 'text-lime-1' : 'text-light-4')} data-id="element-672">
          {currentDisplay.label}
        </span>
      </div>;
  }
  // Imperative verbs for the popover — user is taking an action, not naming
  // the current state.
  const getPopoverDisplay = (s: OfferStatus) => {
    switch (s) {
      case 'saved':
        return {
          icon: <Heart size={18} weight="fill" className="text-lime-1" data-id="element-673" />,
          label: 'Salvează'
        };
      case 'postponed':
        return {
          icon: <Clock size={18} weight="regular" className="text-light-3" data-id="element-674" />,
          label: 'Amână'
        };
      case 'rejected':
        return {
          icon: <XCircle size={18} weight="fill" className="text-red-1" data-id="element-675" />,
          label: 'Respinge'
        };
      default:
        return getReceiverDisplay(s);
    }
  };
  const availableStatuses: OfferStatus[] = (['saved', 'postponed', 'rejected'] as OfferStatus[]).filter(s => s !== status);
  return <div className="relative" ref={popoverRef} data-id="element-676">
      <button type="button" onClick={handleToggle} className={cn('inline-flex items-center gap-1 h-9 px-2.5 rounded-lg bg-dark-3 hover:bg-dark-4 transition-colors cursor-pointer', isOpen && 'bg-dark-4')} title={`${currentDisplay.label} · Click pentru a schimba`} aria-label={`Status: ${currentDisplay.label}. Click pentru a schimba.`} data-id="element-677">
        {currentDisplay.icon}
        <CaretDown size={12} weight="bold" className={cn('text-light-3 transition-transform duration-200', isOpen && 'rotate-180')} data-id="element-678" />
      </button>
      {isOpen && <div className="absolute right-0 top-full mt-1.5 w-40 bg-dark-2 border border-dark-4 rounded-xl shadow-2xl overflow-hidden z-20 py-1 animate-in fade-in slide-in-from-top-1 duration-150" data-id="element-679">
          {availableStatuses.map(s => {
        const display = getPopoverDisplay(s);
        return <button key={s} type="button" onClick={e => handleSelect(e, s)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-dark-3 transition-colors text-left cursor-pointer" data-id="element-680">
                {display.icon}
                <span className="text-light-2 font-medium" data-id="element-681">
                  {display.label}
                </span>
              </button>;
      })}
        </div>}
    </div>;
}